import { CLOSE_MENU, MENU_RESIZE, TOGGLE_ADVANCED_MODE, LOGGED_OUT, SHOW_HELP_MODAL } from '@reduxLocal/actions/types';
import React, { useEffect, useMemo, useRef, useCallback, useContext } from 'react'; // Make sure to import all hooks used
import { View, Text, Pressable, useWindowDimensions, Platform, StyleSheet, ScrollView } from 'react-native';
import styles from '@stylesheet';
import { useSelector, useDispatch, connect } from 'react-redux';
import { DefaultRootState, store, persistor } from '@reduxLocal/persistState';
import LottieView from 'lottie-react-native';
import { useLottieAnim } from '@hooks/lottieHook';
import superAdmin from './Components/DynamicMenuList';
import { LinearGradient } from 'expo-linear-gradient';

import StaticMenuHeader from './Components/StaticMenuHeader';
import PrimaryList from './Components/PrimaryList';
import { useNavigation } from '@react-navigation/native';
import SecondaryMenuList from './subcomponents/SecondaryMenuList';
import PrimaryMenu from './Components/PrimaryMenu';
import apis from '@api/api';
import MenuWarper, { MenuWarperHandle } from './Components/MenuWarp';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { SmartSaaSIcon, SmastSaaSWhite } from '@assets/--native';
import Constants from 'expo-constants';
import { Separator, YStack } from 'tamagui';
import webTransition from '@utils/webTransition';
import { selectMenuOpen } from '@reduxLocal/selectors/menuSelectors';
import { useTranslation } from 'react-i18next';
import * as Animatable from 'react-native-animatable';
import { MaterialIcons } from '@expo/vector-icons';

// ... (The SecondMenu component and webTransition helper remain the same) ...
const SecondMenu = () => {
	const secondaryMenu = useSelector((state: DefaultRootState) => state.UI.secondaryMenuOpen);
	const theme = themeSelector();

	const secondMenuStyle = {
		position: 'relative',
		maxWidth: secondaryMenu ? 80 : 0,
		minWidth: secondaryMenu ? 60 : 0,
		borderRadius: 20,
		borderTopStartRadius: 0,
		borderBottomStartRadius: 0,
		marginVertical: "20vh",
		backgroundColor: theme ? '#008080' : '#2c6273',
		flexDirection: 'column',
		zIndex: -40,
		flex: 1,
		marginRight: secondaryMenu ? -45 : 0,
		overflow: 'hidden',
		...webTransition('max-width, min-width, margin-right', '500ms'),
	};

	if (!secondaryMenu) return null;

	return (
		<View style={secondMenuStyle}>
			<LinearGradient
				colors={theme ? ['#1E90FF', 'transparent'] : ['#333333', 'black']}
				style={{ overflow: "hidden", flex: 1, borderRadius: 20, borderTopStartRadius: 0, borderBottomStartRadius: 0, justifyContent: 'center', flexDirection: 'column' }}>
				<View style={{ alignSelf: 'center', overflow: "hidden", borderRadius: 20, borderTopStartRadius: 0, borderBottomStartRadius: 0, flex: 1, paddingTop: "8%", alignItems: 'center' }}>
					<SecondaryMenuList />
				</View>
			</LinearGradient>
		</View >
	)
}


const Menu = ({ theme, menuType, messageCount }) => {
	const navigation = useNavigation();
	const dispatch = useDispatch();
	const { width } = useWindowDimensions();
	const { t } = useTranslation();
	const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);

	const isMenuOpenFromState = useSelector(selectMenuOpen);

	// Check if we should use overlay mode for smaller screens
	const isSmallScreen = width <= 700;
	
	// For small screens, use overlay mode; for larger screens, use normal behavior
	const menuOpen = isSmallScreen ? isMenuOpenFromState : (width < 500 ? false : isMenuOpenFromState);

	const animation = useLottieAnim();
	const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
	const menuWarper = useRef<MenuWarperHandle>(null);

	// This is a good example of a Hook that is NOT conditional and is correctly placed.
	const { name, surname } = selectUser();

	const animation3 = useLottieAnim();
	const logVisible = useSelector((state: any) => state.UI.advancedMode);
	const menuContainerRef = useRef(null);

	// Close menu warper when clicking outside
	const handleBackdropClick = useCallback(() => {
		if (menuWarper.current) {
			menuWarper.current.animateOut();
		}
	}, []);

	// ... (the rest of your component logic remains the same) ...
	useEffect(() => {
		const node = menuContainerRef.current;

		const handleTransitionStart = (event) => {
			if (event.propertyName === 'width') {
				dispatch({ type: 'MENU_STATE_CHANGING' })
			}
		};

		const handleTransitionEnd = (event) => {
			if (event.propertyName === 'width') {
				dispatch({ type: 'MENU_STABILISED' });
			}
		};

		if (Platform.OS === 'web' && node) {
			node.addEventListener('transitionstart', handleTransitionStart);
			node.addEventListener('transitionend', handleTransitionEnd);

			return () => {
				node.removeEventListener('transitionstart', handleTransitionStart);
				node.removeEventListener('transitionend', handleTransitionEnd);
			};
		}
	}, [dispatch]); // Added dispatch to dependency array


	const _handleLogout = useCallback(async () => {
		navigation.navigate('Loading', { params: { flagHold: true } });
		dispatch({ type: LOGGED_OUT });
		await persistor.purge();
		await apis.logoutUser()
	}, [navigation, dispatch]);

	const defaultMenuArray = useMemo(() => {
		if (currentCompany) {
			const newSuperAdmin = JSON.parse(JSON.stringify(superAdmin));
			const settingsMenu = newSuperAdmin.find(m => m.menuType === menuType)?.menu.find(s => s.screen === 'Settings');
			if (settingsMenu) {
				settingsMenu.secondaryMenu.content.unshift({
					id: 103, screen: 'Company', parent: 'Settings', header: 'Company Profile',
					icon: require("@assets/animations/company.json"), mobileIcon: null, state: false
				});
			}
			return newSuperAdmin;
		}
		return superAdmin;
	}, [currentCompany, menuType]);

	const filteredMenu = defaultMenuArray.filter(menu => menu.menuType === menuType);

	const _showWarper = useCallback(() => {
		menuWarper.current?.toggleVisibility?.();
	}, []);

	useEffect(() => {
		const handleResize = () => {
			// Close menu only if width is less than 500px (very small screens)
			// For screens between 500-700px, allow overlay mode
			if (window.innerWidth < 500) {
				dispatch({ type: CLOSE_MENU });
			}
		};
		window.addEventListener('resize', handleResize);
		handleResize();
		const unsubscribe = store.subscribe(() => {
			const action = store.getState().lastAction;
			if (action?.type === 'OPEN_WARPER' || action?.type === 'CLOSE_WARPER') {
				_showWarper();
			}
		});
		return () => {
			window.removeEventListener('resize', handleResize);
			unsubscribe();
		};
	}, [dispatch, _showWarper]);

	// This early return is safe because it's after all Hook calls.
	if (!filteredMenu.length || !filteredMenu[0].menu) return null;
	const newMenuArray = filteredMenu[0].menu;

	// ... (rest of your return statement and styles are the same) ...

	const menuContainerStyle = {
		width: menuOpen ? 270 : 70,
		minWidth: 70,
		borderRightWidth: 0.5,
		borderRightColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
		marginLeft: isSmallScreen ? -10 : -10,
		flexDirection: 'column',
		zIndex: isSmallScreen ? 1000 : 100,
		position: isSmallScreen && menuOpen ? 'absolute' : 'relative',
		top: isSmallScreen && menuOpen ? 0 : 'auto',
		left: isSmallScreen && menuOpen ? 0 : 'auto',
		height: isSmallScreen && menuOpen ? '100vh' : 'auto',
		shadowColor: isSmallScreen && menuOpen ? '#000' : 'transparent',
		shadowOffset: isSmallScreen && menuOpen ? { width: 2, height: 0 } : { width: 0, height: 0 },
		shadowOpacity: isSmallScreen && menuOpen ? 0.25 : 0,
		shadowRadius: isSmallScreen && menuOpen ? 10 : 0,
		elevation: isSmallScreen && menuOpen ? 10 : 0,
		overflow: 'hidden',
		...webTransition('width')
	};

	const logoTextStyle = {
		width: menuOpen ? 120 : 0,
		opacity: menuOpen ? 1 : 0,
		overflow: 'hidden',
		...webTransition('width, opacity')
	};

	const menuLabelStyle = {
		opacity: menuOpen ? 1 : 0,
		flex: 1,
		textAlign: 'center',
		color: theme ? "#333333" : 'white',
		fontWeight: 600,
		justifyContent: 'center',
		fontSize: 16,
		...webTransition('opacity')
	};

	console.log("[Fast Refresh] Component reloaded at", new Date().toISOString());


	return (
		<View style={{ zIndex: 10, height: '100%', flexDirection: 'row' }}>
			{/* Backdrop overlay for small screens */}
			{isSmallScreen && menuOpen && (
				<Pressable 
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						zIndex: 999
					}}
					onPress={() => dispatch({ type: CLOSE_MENU })}
				/>
			)}
			
			{/* Main Menu Container */}
			<View style={{ flex: 1, flexDirection: 'row' }}>
				<LinearGradient
					colors={['#C8A2C8', '#87CEEB']}
					start={{ x: 0, y: 0 }}
					end={{ x: 1, y: 1 }}
					style={menuContainerStyle}
				>
					{/* Floating blur elements for depth */}
					{Platform.OS === 'web' && (
						<>
							<View style={{
								position: 'absolute',
								top: '20%',
								left: '10%',
								width: 100,
								height: 100,
								backgroundColor: theme ? 'rgba(59, 130, 246, 0.2)' : 'rgba(147, 197, 253, 0.15)',
								borderRadius: 50,
								...(Platform.OS === 'web' && {
									filter: 'blur(40px)',
								}),
								zIndex: 0,
							}} />
							<View style={{
								position: 'absolute',
								bottom: '30%',
								right: '15%',
								width: 120,
								height: 120,
								backgroundColor: theme ? 'rgba(200, 162, 200, 0.25)' : 'rgba(135, 206, 235, 0.2)',
								borderRadius: 60,
								...(Platform.OS === 'web' && {
									filter: 'blur(50px)',
								}),
								zIndex: 0,
							}} />
						</>
					)}
					<View
						// @ts-ignore - ref is valid for web to get DOM node
						ref={menuContainerRef}
						style={{
							flex: 1,
							backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
							...(Platform.OS === 'web' && {
								backdropFilter: 'blur(20px)',
							}),
							zIndex: 1,
						}}
					>
					{/* Floating close button for overlay mode */}
					{isSmallScreen && menuOpen && (
						<Pressable
							style={{
								position: 'absolute',
								top: 10,
								right: 10,
								zIndex: 1001,
								backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
								borderRadius: 20,
								width: 40,
								height: 40,
								justifyContent: 'center',
								alignItems: 'center',
								shadowColor: '#000',
								shadowOffset: { width: 0, height: 2 },
								shadowOpacity: 0.25,
								shadowRadius: 4,
								elevation: 5
							}}
							onPress={() => dispatch({ type: CLOSE_MENU })}
						>
							<MaterialIcons
								name="close"
								size={24}
								color={theme ? "#333333" : "white"}
							/>
						</Pressable>
					)}
						<View style={[styles.centerEverything, { alignSelf: "stretch", minWidth: "100%", minHeight: 60, maxHeight: 60, paddingTop: 15, paddingHorizontal: 30, overflow: "visible", backgroundColor: 'transparent', flexDirection: "row", ...(Platform.OS === 'web' && { backdropFilter: 'blur(10px)' }) }]}>
						<Pressable onLongPress={() => dispatch({ type: TOGGLE_ADVANCED_MODE })} onPress={() => navigation.navigate('Dashboard')} style={[styles.centerEverything, { flex: 1, alignSelf: 'stretch', padding: 2, left: 4, maxWidth: 70 }]}>
							{
								theme ?
									<SmartSaaSIcon height={30} width={30} />
									:
									<SmastSaaSWhite height={30} width={30} />
							}
							{logVisible && <Text style={{ color: theme ? "#333333" : "white" }}>Advanced</Text>}
						</Pressable>
						<View style={[logoTextStyle,{ top: -8}]}>
								<View style={{ gap: 5, backgroundColor: theme ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, ...(Platform.OS === 'web' && { backdropFilter: 'blur(8px)' }) }}>
									<Text style={{ userSelect: "none", fontSize: 14, fontWeight: "bold", textAlign: 'center', color: theme ? "#1e293b" : "#f1f5f9" }}>SmartSaaS</Text>
									<Text style={{ userSelect: "none", fontSize: 12, textAlign: 'center', color: theme ? "#475569" : "#cbd5e1" }}>Version: {Constants.expoConfig?.version}</Text>
							</View>
						</View>
					</View>
					<YStack >
						<View style={[styles.centerEverything, { left: !menuOpen && 5, marginBottom: 10,  alignSelf: "stretch", flex: 1, zIndex: 2,  ...webTransition('flex, max-height') }]}>
						<StaticMenuHeader filteredMenu={filteredMenu} newMenuArray={newMenuArray} theme={theme} menuOpen={menuOpen} navigation={navigation} messageCount={messageCount} />
					</View >
							<LinearGradient
								colors={['#C8A2C8', '#87CEEB']}
								start={{ x: 0, y: 0 }}
								end={{ x: 1, y: 1 }}
								style={{
									maxHeight: 70,
									top: -15,
									paddingHorizontal: 10,
									paddingRight: 0,
									borderTopEndRadius: 0,
									borderBottomEndRadius: 0,
									alignSelf: 'stretch',
									borderWidth: 1,
									borderColor: theme
										? 'rgba(200, 162, 200, 0.3)'
										: 'rgba(135, 206, 235, 0.3)',
									shadowColor: '#000',
									shadowOffset: { width: 0, height: 4 },
									shadowOpacity: theme ? 0.1 : 0.3,
									shadowRadius: 12,
									elevation: 8,
									overflow: 'hidden',
									position: 'relative',
								}}
							>
								{/* Floating blur elements for depth */}
								{Platform.OS === 'web' && (
									<View style={{
										position: 'absolute',
										top: '50%',
										left: '50%',
										transform: 'translate(-50%, -50%)',
										width: 30,
										height: 60,
										backgroundColor: theme ? 'rgba(59, 130, 246, 0.2)' : 'rgba(147, 197, 253, 0.15)',
										borderRadius: 30,
										...(Platform.OS === 'web' && {
											filter: 'blur(30px)',
										}),
										zIndex: 0,
									}} />
								)}
								<View style={{
									flex: 1,
									backgroundColor: theme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
									...(Platform.OS === 'web' && {
										backdropFilter: 'blur(10px)',
									}),
									zIndex: 1,
								}}>
									<Pressable
									id="tutorial-menu-warper-button"
										onPress={_showWarper}
										onHoverIn={() => { animation3.current?.reset(), animation3.current?.play() }}
										style={{ flex: 1, minHeight: 40, flexDirection: 'row', alignContent: 'center', alignItems: 'center', gap: 5 }}
										hitSlop={15}>
										{(hovered) => (
											<Animatable.View useNativeDriver transition={'backgroundColor'} style={{ flex: 1, flexDirection: 'row', backgroundColor: hovered ? (theme ? "rgba(200, 162, 200, 0.4)" : "rgba(135, 206, 235, 0.4)") : "transparent", borderRadius: 10, padding: 10, minHeight: 40, maxHeight: 40, maxWidth: "100%" }}>
												<LottieView key={4} ref={animation3} style={[{ borderRadius: 20, width: 40, borderRadius: 100 }]} source={require('@assets/animations/1865-shooting-stars-outline-edited.json')}
													loop={false} />
												<Text selectable={false} style={[menuLabelStyle, { color: theme ? "#1e293b" : "#f1f5f9", left: -12, fontWeight: '600' }]}>{filteredMenu[0].menuLabel}</Text>
											</Animatable.View>
										)}
									</Pressable>
								</View>
							</LinearGradient>
					</YStack>
					<MenuWarper ref={menuWarper} />
					<View style={{ flex: 10, flexDirection: 'column', zIndex: 1,  }}>
						<ScrollView
							style={{ flex: 1.5, left: menuOpen ? undefined: 8, marginBottom: 10 }}
							showsVerticalScrollIndicator={false}
							contentContainerStyle={{ paddingLeft: !isSmallScreen ? 0 : 5, marginLeft: !isSmallScreen ? 0 : 5 }}
						>
							<PrimaryList />
						</ScrollView>
							{/* <View style={{ left: menuOpen ? 10 : 15, alignSelf: 'stretch', justifyContent: 'flex-end', borderTopColor: 'rgba(255,255,255,0.5)', alignContent: 'flex-end', transform: [{ scale: 1.2 }] }}>
							<PrimaryMenu noBackColor item={{ key: 'user', icon: require('@assets/animations/LordIcons/logout.json'), header: 'logout', function: _handleLogout }} />
						</View> */}
					</View>
					</View>
				</LinearGradient>
			</View>
		</View>
	);
}

const mapStateToProps = function (state) {
	return {
		theme: state.app.theme.lightTheme,
		mailAuthenticated: state.app.mail.mailAuthenticated,
		accessToken: state.promiseData.google?.googleCredentials?.accessToken,
		menuType: state.navigation.menuType,
		messageCount: state.app.mail.mailCount,
		accessLevel: state.promiseData.data.userData.access
	}
}

export default connect(mapStateToProps)(Menu);